import { eventBus } from "@/lib/utils/eventBus";
import type { VoicePilotAction } from "./voicePilot";

export interface ActionResult {
  success: boolean;
  message: string;
}

function normalizeUrl(url: string): string {
  if (!url) return "";
  let u = url.trim();
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.includes(".") && !u.includes(" ")) return "https://" + u;
  return "";
}

function openUrl(url: string): boolean {
  if (typeof window === "undefined") return false;
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (win) win.opener = null;
  return !!win;
}

// Real OS/app-registered URI schemes — only apps that register their own
// scheme can be launched from a web page at all. Add more here as needed,
// but don't invent schemes that don't exist.
const KNOWN_APP_SCHEMES: Record<string, string> = {
  spotify: "spotify:",
  "vs code": "vscode://",
  vscode: "vscode://",
  slack: "slack://open",
  notion: "notion://",
  zoom: "zoommtg://",
  discord: "discord://",
  whatsapp: "whatsapp://",
  telegram: "tg://",
  finder: "file:///",
};

export function executeVoicePilotAction(action: VoicePilotAction): ActionResult {
  try {
    console.log("[executeVoicePilotAction]", action.type, action);
    switch (action.type) {
      case "openChatTile": {
        const profileId = action.profileId;
        if (!profileId) return { success: false, message: "Missing profileId for openChatTile" };
        eventBus.emit("SPATIAL:OPEN_CHAT_WITH_PROFILE", { profileId });
        return { success: true, message: `Opened chat with ${action.profileName ?? profileId}` };
      }

      case "openNamedChatTile": {
        const aiName = action.aiName;
        if (!aiName) return { success: false, message: "Missing aiName for openNamedChatTile" };
        eventBus.emit("SPATIAL:OPEN_CHAT_WITH_PROFILE", { profileId: aiName });
        return { success: true, message: `Opened chat with ${aiName}` };
      }

      case "broadcastToAllTiles": {
        const message = action.message;
        if (!message) return { success: false, message: "Missing message for broadcastToAllTiles" };
        eventBus.emit("SPATIAL:TILE_BROADCAST", { message, tileIds: undefined });
        return { success: true, message: `Broadcasted to all tiles` };
      }

      case "sendToChatTile": {
        const tileId = action.tileId;
        const message = action.message;
        if (!tileId || !message) return { success: false, message: "Missing tileId or message for sendToChatTile" };
        eventBus.emit("SPATIAL:SEND_TO_TILE", { tileId, message });
        return { success: true, message: `Sent message to tile ${tileId}` };
      }

      case "quickCommand": {
        const text = action.text;
        if (!text) return { success: false, message: "Missing text for quickCommand" };
        eventBus.emit("SPATIAL:QUICK_COMMAND", { text });
        return { success: true, message: `Ran quick command: ${text}` };
      }

      case "openBuilder": {
        eventBus.emit("BUILDER:OPEN");
        return { success: true, message: "Opened builder" };
      }

      case "openShelf": {
        eventBus.emit("SHELF:OPEN");
        return { success: true, message: "Opened neural shelf" };
      }

      case "openThoughtStream": {
        eventBus.emit("MINDALOO:OPEN_PANEL", { panel: "thought-stream" });
        return { success: true, message: "Opened thought stream" };
      }

      case "setAwareness": {
        const state = action.state;
        if (!state) return { success: false, message: "Missing state for setAwareness" };
        eventBus.emit("AWARENESS:SET", { state });
        return { success: true, message: `Set awareness to ${state}` };
      }

      case "createStickyNote": {
        const content = action.content;
        if (!content) return { success: false, message: "Missing content for createStickyNote" };
        eventBus.emit("SPATIAL:CREATE_STICKY", { content });
        return { success: true, message: "Created sticky note" };
      }

      case "themeToggle": {
        eventBus.emit("THEME:TOGGLE");
        return { success: true, message: "Toggled theme" };
      }

      case "dismissAllPanels": {
        eventBus.emit("MINDALOO:DISMISS_ALL");
        return { success: true, message: "Dismissed all panels" };
      }

      case "arrangeTiles": {
        const mode = action.mode === "stack" ? "stack" : "grid";
        eventBus.emit("SPATIAL:ARRANGE_TILES", { mode });
        return { success: true, message: `Arranged tiles in a ${mode}` };
      }

      case "closeTile": {
        const tileId = action.tileId;
        if (!tileId) return { success: false, message: "Missing tileId for closeTile" };
        eventBus.emit("SPATIAL:REMOVE_TILE", { id: tileId });
        return { success: true, message: `Closed tile ${tileId}` };
      }

      case "openWebsite": {
        const url = normalizeUrl(action.url);
        if (!url) return { success: false, message: "Missing or invalid URL for openWebsite" };
        const opened = openUrl(url);
        return { success: opened, message: opened ? `Opened ${url}` : "Failed to open browser tab" };
      }

      case "searchWeb": {
        const query = action.query;
        if (!query) return { success: false, message: "Missing query for searchWeb" };
        // window.open() here was silently blocked every time — it fires
        // from deep inside an async chain (mic -> API -> action execution),
        // well after any user gesture, which is exactly what popup blockers
        // exist to stop. Confirmed live: the action ran every time, nothing
        // ever appeared. Route through the same tested pipeline as
        // generateImage instead — no popup involved at all. Note: this
        // answers from the model's own knowledge, not a live web search —
        // there's no real search API wired into per-tile chat yet, so
        // "current news" style queries may be stale. That's a real
        // capability gap, not something this fix papers over.
        eventBus.emit("SPATIAL:QUICK_COMMAND", { text: `Search the web for: ${query}` });
        return { success: true, message: `Searching for: ${query}` };
      }

      case "generateImage": {
        const prompt = action.prompt;
        if (!prompt) return { success: false, message: "Missing prompt for generateImage" };
        // Reuse the exact same tested pipeline as typing into a chat tile —
        // the task classifier detects "generate an image" phrasing and
        // routes it to real image generation, rendered via PolaroidImage.
        eventBus.emit("SPATIAL:QUICK_COMMAND", { text: `Generate an image: ${prompt}` });
        return { success: true, message: `Generating image: ${prompt}` };
      }

      case "openExternalApp": {
        const appName = action.appName;
        if (!appName) return { success: false, message: "Missing appName for openExternalApp" };
        // A web page can't launch arbitrary native apps — "openapp://" was a
        // made-up scheme nothing on earth handles, so this always silently
        // did nothing. Real app-launch URIs only exist for apps that
        // register their own scheme; unknown apps get an honest failure
        // instead of a fabricated success.
        const scheme = KNOWN_APP_SCHEMES[appName.toLowerCase().trim()];
        if (!scheme) {
          return { success: false, message: `I don't have a way to open ${appName} from the browser.` };
        }
        // window.open()'s return value doesn't reliably reflect whether the
        // OS actually launched the app for custom URI schemes — treat this
        // as "requested", not "confirmed opened".
        openUrl(scheme);
        return { success: true, message: `Requested ${appName} (can't confirm it actually opened)` };
      }

      case "createReminder": {
        const date = action.date;
        const time = action.time;
        const reminderMessage = action.message;
        if (!date || !time || !reminderMessage) {
          return { success: false, message: "Missing date, time, or message for createReminder" };
        }
        eventBus.emit("KARMA:CREATE_REMINDER", { date, time, message: reminderMessage });
        return { success: true, message: `Reminder set for ${date} ${time}` };
      }

      case "saveMemory": {
        const category = action.category;
        const key = action.key;
        const value = action.value;
        if (!category || !key || value === undefined) {
          return { success: false, message: "Missing category, key, or value for saveMemory" };
        }
        eventBus.emit("KARMA:SAVE_MEMORY", { category, key, value });
        return { success: true, message: "Saved to memory" };
      }

      case "systemStatus": {
        const memory = navigator && (navigator as any).deviceMemory ? `${(navigator as any).deviceMemory} GB` : "unknown";
        const cores = navigator?.hardwareConcurrency || "unknown";
        const online = navigator?.onLine ? "online" : "offline";
        const status = `Browser memory: ${memory}, CPU cores: ${cores}, Network: ${online}`;
        eventBus.emit("KARMA:SYSTEM_STATUS", { status });
        return { success: true, message: status };
      }

      case "shutdownKarma": {
        eventBus.emit("KARMA:VOICE_STOP");
        return { success: true, message: "Voice session stopped" };
      }

      default:
        return { success: false, message: `Unknown action type: ${action.type}` };
    }
  } catch (error: any) {
    return { success: false, message: error.message || "Action execution failed" };
  }
}

export function executeVoicePilotActions(actions: VoicePilotAction[]): ActionResult[] {
  return actions.map(executeVoicePilotAction);
}
